import { useCallback, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { getUserInfo, sessionCheck, loggedInFalse } from 'app/store/modules/auth';
import dayjs from 'app/libs/dayjs';
import * as api from 'app/api';
import { clearAuthInfo } from 'app/hook/login/useLogout';
import { goHome } from 'app/libs/location';
import { ACCESS_TOKEN_KEY } from 'app/libs/auth';
import useSsrCookie from 'app/hook/common/useSsrCookie';
import useToken from './useToken';
import useGoLogin from './useGoLogin';

export default function useAuth() {
	const { getCookie, setCookie } = useSsrCookie();
	const [initAuth, setInitAuth] = useState(false);
	const dispatch = useDispatch();
	const { getTokenExpire, getTokenPayload, renewToken } = useToken();
	const goLogin = useGoLogin();
	const { isLoggedIn, user, userInfo } = useSelector((state) => state.auth, shallowEqual);

	const redirect = useCallback(() => {
		goHome();
	}, []);

	const sessionCheckInfo = useCallback(() => dispatch(sessionCheck()), [dispatch]);

	const getUser = useCallback(
		async ({ checkAuth = false, passAuth = false, authOptions = {} }) => {
			if (checkAuth) {
				const { authToHome, unAuthToLogin, useAlertCheckAuth, destroyWebViewAfterLogin } = authOptions;

				try {
					const authSession = getCookie('X-Auth-Token-Encar');
					const authToken = getCookie(ACCESS_TOKEN_KEY);

					if (!authSession) {
						throw new Error('Not found auth info');
					}

					// 업데이트 안된 앱을 위한 임시코드
					if (!authToken) {
						const tokenBySessionResponse = await api.getAccessTokenBySession(authSession);
						const { apiKey = {} } = tokenBySessionResponse.data || {};
						setCookie(ACCESS_TOKEN_KEY, apiKey.accessToken);
					}
					await Promise.all([dispatch(getUserInfo()), dispatch(sessionCheck())]);

					const tokenExpire = dayjs(getTokenExpire());
					if (dayjs().isAfter(tokenExpire.add(-1, 'hour'))) {
						renewToken();
					}
					setInitAuth(true);
				} catch (err) {
					clearAuthInfo();
					dispatch(loggedInFalse());

					if (passAuth) {
						setInitAuth(true);
					} else if (authToHome) {
						setInitAuth(true);
					} else if (unAuthToLogin) {
						goLogin({
							replace: true,
							returnUrl: encodeURIComponent(window.location.href),
							useAlertCheckAuth,
							destroyWebViewAfterLogin,
						});
					} else {
						redirect();
					}
				}
			} else {
				setInitAuth(true);
			}
		},
		[dispatch, redirect, getTokenExpire, renewToken, goLogin, getCookie, setCookie],
	);

	const userId = userInfo?.id;
	const checkIsChangedLoginUser = useCallback(() => userId !== getTokenPayload()?.user?.id, [userId, getTokenPayload]);

	return {
		initAuth,
		isLoggedIn,
		user,
		userInfo,
		getUser,
		redirect,
		sessionCheckInfo,
		checkIsChangedLoginUser,
	};
}
